import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import { ReviewAdminFeedbackAPI } from '../../../../api/AdminAPI';
import {
  FEEDBACK_STATUS_OPTIONS,
  FEEDBACK_CLASSIFICATION_OPTIONS,
  FEEDBACK_PRIORITY_OPTIONS,
  FEEDBACK_NOTIFY_STATUSES,
} from './FeedbackBadges';
import { formatDate } from '../../../../utils/Utils';

const fieldSx = { '& .MuiOutlinedInput-root': { borderRadius: '8px' } };

const fromItem = (item) => ({
  status: item.status || 'received',
  classification: item.classification || '',
  priority: item.priority || '',
  internal_notes: item.internal_notes || '',
  response_text: item.response_text || '',
  fixed_commit: item.fixed_commit || '',
});

function fmtDate(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr.replace(' ', 'T'));
  return Number.isNaN(date.getTime()) ? '' : formatDate(date);
}

/**
 * FeedbackReviewForm - Revisão de um pedido de problema/melhoria (Admin Geral)
 *
 * Status, classificação, prioridade, nota interna, commit da correção e a resposta ao
 * usuário. Ao escolher Disponível / Respondido / No roadmap a resposta é pré-preenchida
 * com o texto padrão do backend (item.default_responses) e o botão avisa que o salvar
 * manda o aviso no sino e por e-mail (uma vez por status).
 *
 * @param {Object} props
 * @param {Object} props.item - pedido completo (get.php)
 * @param {Function} props.onSaved - (response) => void, response = { item, notified }
 */
function FeedbackReviewForm({ item, onSaved }) {
  const [form, setForm] = useState(() => fromItem(item));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setForm(fromItem(item));
    setError('');
  }, [item]);

  const set = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleStatusChange = (e) => {
    const status = e.target.value;
    setForm((prev) => {
      const defaults = item.default_responses || {};
      const prevDefault = defaults[prev.status] || '';
      // troca o texto padrão junto com o status, sem apagar o que a equipe escreveu
      // (o texto do aviso já enviado também é trocado: ele era do status anterior)
      const sentText = item.notified_status ? item.response_text : null;
      const keepsTyped = prev.response_text.trim() !== ''
        && prev.response_text !== prevDefault
        && prev.response_text !== sentText;
      const response_text = keepsTyped ? prev.response_text : (defaults[status] || '');
      return { ...prev, status, response_text };
    });
  };

  const isNotifyStatus = FEEDBACK_NOTIFY_STATUSES.includes(form.status);
  const willNotify = isNotifyStatus && item.notified_status !== form.status;
  const alreadyNotified = isNotifyStatus && item.notified_status === form.status;

  const handleSave = () => {
    setSaving(true);
    setError('');
    ReviewAdminFeedbackAPI(item.id, form, (response) => {
      setSaving(false);
      if (!response || response.success === false) {
        setError(response?.error || 'Erro ao salvar a revisão. Tente novamente.');
        return;
      }
      if (onSaved) onSaved(response);
    });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>Revisão</Typography>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 1.5 }}>
        <TextField select size="small" label="Status" value={form.status} onChange={handleStatusChange} sx={fieldSx}>
          {FEEDBACK_STATUS_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
          ))}
        </TextField>
        <TextField select size="small" label="Classificação" value={form.classification} onChange={set('classification')} sx={fieldSx}>
          <MenuItem value="">Sem classificação</MenuItem>
          {FEEDBACK_CLASSIFICATION_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
          ))}
        </TextField>
        <TextField select size="small" label="Prioridade" value={form.priority} onChange={set('priority')} sx={fieldSx}>
          <MenuItem value="">Sem prioridade</MenuItem>
          {FEEDBACK_PRIORITY_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
          ))}
        </TextField>
      </Box>

      <TextField
        size="small"
        label="Nota interna"
        helperText="Só a equipe vê: análise, causa, como reproduzir."
        value={form.internal_notes}
        onChange={set('internal_notes')}
        multiline
        minRows={2}
        sx={fieldSx}
      />

      <TextField
        size="small"
        label="Commit da correção"
        value={form.fixed_commit}
        onChange={set('fixed_commit')}
        sx={fieldSx}
      />

      {isNotifyStatus && (
        <TextField
          size="small"
          label="Resposta ao usuário"
          helperText={alreadyNotified
            ? `Usuário avisado ${fmtDate(item.notified_at) ? `em ${fmtDate(item.notified_at)}` : ''} com este texto.`
            : `Vai para o sino e para o e-mail ${item.user_email || 'de quem enviou'}.`}
          value={form.response_text}
          onChange={set('response_text')}
          multiline
          minRows={3}
          inputProps={{ maxLength: 4000 }}
          sx={fieldSx}
        />
      )}

      {error && <Alert severity="error">{error}</Alert>}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600 }}
        >
          {willNotify ? 'Salvar e avisar o usuário' : 'Salvar'}
        </Button>
      </Box>
    </Box>
  );
}

export default FeedbackReviewForm;
